import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList, Building2, MapPin, Users, Plus, X, ChevronDown, ChevronRight,
  Save, Download, Send, Copy, Check, Minus, Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { buildShootPdf, shootWhatsAppUrl } from "@/pages/shoots/shootHelpers";

const emptyForm = {
  client: "", shoot_date: "", call_time: "",
  location_type: "", location: "",
  shoot_type: "",
  crew: [],           // [{name, is_manager?}]
  notes: "",
  rental_equipment: "",
  items: {},          // {itemKey: {category, name, qty, photo_url}}
};

const itemKey = (item) => `${item.category}::${item.name}`;

export default function NewShoot() {
  const nav = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const canView = !!user && (user.is_super_admin || user.is_admin || user.team === "Shoot Team");

  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [recentShoots, setRecentShoots] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, shoot_date: params.get("date") || "" });
  const [openCat, setOpenCat] = useState({}); // {catName: boolean}
  const [newCrew, setNewCrew] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [applyOpts, setApplyOpts] = useState({ equipment: true, crew: false });
  const [loadingShoot, setLoadingShoot] = useState(false);

  // Load lookup data (categories / items / users / shoot archive) ONCE on
  // mount. Previously the whole bundle re-fetched every time `id` changed,
  // which is why switching between saved shoots felt sluggish.
  useEffect(() => {
    if (!canView) return;
    Promise.all([
      api.get("/shoots/equipment/categories"),
      api.get("/shoots/equipment/items"),
      api.get("/users"),
      api.get("/shoots"),
    ]).then(([c, i, u, s]) => {
      setCats(c.data || []);
      setItems(i.data || []);
      setUsers(u.data || []);
      setRecentShoots(s.data || []);
    }).catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load"));
  }, [canView]);

  // Load the specific shoot when the id in the URL changes. The archive list
  // already includes items/crew so we hydrate instantly from cache if we
  // have it and only hit the network for the authoritative copy.
  useEffect(() => {
    if (!canView) return;
    if (!id) {
      // Honour any prefill snapshot queued by "Copy for next day" from an
      // existing edit — see applyPrevious() below.
      let prefill = null;
      try {
        const raw = sessionStorage.getItem("lim-shoot-prefill");
        if (raw) {
          prefill = JSON.parse(raw);
          sessionStorage.removeItem("lim-shoot-prefill");
        }
      } catch { /* ignore */ }
      if (prefill) {
        const itemMap = {};
        (prefill.items || []).forEach((it) => { itemMap[itemKey(it)] = { ...it }; });
        setForm({
          client: prefill.client || "",
          shoot_date: prefill.shoot_date || params.get("date") || "",
          call_time: prefill.call_time || "",
          location_type: prefill.location_type || "",
          location: prefill.location || "",
          shoot_type: prefill.shoot_type || "",
          crew: prefill.crew || [],
          notes: prefill.notes || "",
          rental_equipment: prefill.rental_equipment || "",
          items: itemMap,
        });
      } else {
        setForm({ ...emptyForm, shoot_date: params.get("date") || "" });
      }
      return;
    }
    // Optimistic hydrate from the archive list — eliminates the "blank form
    // for a second" feeling while /shoots/:id round-trips.
    const cached = recentShoots.find((s) => s.id === id);
    if (cached) {
      const itemMap = {};
      (cached.items || []).forEach((it) => { itemMap[itemKey(it)] = { ...it }; });
      setForm({
        client: cached.client || "",
        shoot_date: cached.shoot_date || "",
        call_time: cached.call_time || "",
        location_type: cached.location_type || "",
        location: cached.location || "",
        shoot_type: cached.shoot_type || "",
        crew: cached.crew || [],
        notes: cached.notes || "",
        rental_equipment: cached.rental_equipment || "",
        items: itemMap,
      });
    }
    setLoadingShoot(true);
    api.get(`/shoots/${id}`).then(({ data }) => {
      const itemMap = {};
      (data.items || []).forEach((it) => { itemMap[itemKey(it)] = { ...it }; });
      setForm({
        client: data.client || "",
        shoot_date: data.shoot_date || "",
        call_time: data.call_time || "",
        location_type: data.location_type || "",
        location: data.location || "",
        shoot_type: data.shoot_type || "",
        crew: data.crew || [],
        notes: data.notes || "",
        rental_equipment: data.rental_equipment || "",
        items: itemMap,
      });
    }).catch(() => toast.error("Failed to load shoot"))
      .finally(() => setLoadingShoot(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, id]);

  const setLocationType = (type) => {
    setForm((f) => ({
      ...f,
      location_type: type,
      location: type === "Studio" ? "Life Inside Media Studio, Pune" : "",
    }));
  };

  // ---- Equipment picker helpers ----
  const catOpen = (cat) => !!openCat[cat];
  const toggleCat = (cat) => setOpenCat((s) => ({ ...s, [cat]: !s[cat] }));

  const isPicked = (it) => !!form.items[itemKey(it)];
  const pickedQty = (it) => form.items[itemKey(it)]?.qty ?? 0;

  const togglePick = (it) => {
    setForm((f) => {
      const key = itemKey(it);
      const next = { ...f.items };
      if (next[key]) delete next[key];
      else next[key] = { item_id: it.id, category: it.category, name: it.name, qty: it.default_qty || 1, photo_url: it.photo_url };
      return { ...f, items: next };
    });
  };
  const stepQty = (it, delta) => {
    setForm((f) => {
      const key = itemKey(it);
      const cur = f.items[key];
      const next = { ...f.items };
      if (!cur) {
        next[key] = { item_id: it.id, category: it.category, name: it.name, qty: Math.max(1, (it.default_qty || 1) + delta), photo_url: it.photo_url };
      } else {
        const newQty = Math.max(0, cur.qty + delta);
        if (newQty === 0) delete next[key];
        else next[key] = { ...cur, qty: newQty };
      }
      return { ...f, items: next };
    });
  };

  const selectAllInCategory = (cat) => {
    setForm((f) => {
      const next = { ...f.items };
      items.filter((i) => i.category === cat).forEach((it) => {
        const key = itemKey(it);
        if (!next[key]) next[key] = { item_id: it.id, category: it.category, name: it.name, qty: it.default_qty || 1, photo_url: it.photo_url };
      });
      return { ...f, items: next };
    });
  };
  const clearCategory = (cat) => {
    setForm((f) => {
      const next = { ...f.items };
      Object.keys(next).forEach((k) => { if (next[k].category === cat) delete next[k]; });
      return { ...f, items: next };
    });
  };

  // ---- Crew helpers ----
  const addCrew = () => {
    const nm = newCrew.trim();
    if (!nm) return;
    setForm((f) => ({ ...f, crew: [...f.crew, { name: nm }] }));
    setNewCrew("");
  };
  const removeCrew = (idx) => setForm((f) => ({ ...f, crew: f.crew.filter((_, i) => i !== idx) }));

  // ---- Continue from previous shoot ----
  // When creating a NEW draft (no id), copying merges the picked shoot's
  // equipment/crew into the current form.
  // When EDITING an existing shoot, copying starts a fresh draft prefilled
  // with that shoot's details and auto-dates it to the next day — this is
  // the "extend the same shoot list for next days" workflow.
  const applyPrevious = (prev) => {
    if (id) {
      try {
        // Build a prefill snapshot the fresh "/shoots/new" page will pick up.
        const base = new Date(prev.shoot_date || form.shoot_date || Date.now());
        base.setDate(base.getDate() + 1);
        const nextDay = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(base.getDate()).padStart(2, "0")}`;
        sessionStorage.setItem("lim-shoot-prefill", JSON.stringify({ ...prev, shoot_date: nextDay }));
      } catch { /* storage disabled — fall back to blank draft */ }
      toast.success(`Starting a fresh draft from ${prev.client} — set for the next day`);
      nav("/shoots/new");
      return;
    }
    setForm((f) => {
      const next = { ...f };
      if (applyOpts.equipment && prev.items?.length) {
        const map = {};
        prev.items.forEach((it) => { map[itemKey(it)] = { ...it }; });
        next.items = map;
      }
      if (applyOpts.crew && prev.crew?.length) {
        next.crew = prev.crew;
      }
      return next;
    });
    toast.success(`Copied from ${prev.client} · ${prev.shoot_date}`);
  };

  // ---- Delete a saved shoot from the archive ----
  const deleteShoot = async (shoot) => {
    const label = `${shoot.client || "this shoot"} · ${shoot.shoot_date || ""}`.trim();
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/shoots/${shoot.id}`);
      setRecentShoots((prev) => prev.filter((s) => s.id !== shoot.id));
      toast.success(`Deleted ${label}`);
      // If we were editing the shoot we just deleted, kick the user back to the
      // "new shoot" screen so they don't hit a stale edit form.
      if (id === shoot.id) nav("/shoots/new");
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to delete shoot");
    }
  };

  // ---- Save ----
  const validReady = form.client.trim() && form.shoot_date && form.location_type && Object.keys(form.items).length > 0;
  const buildPayload = () => ({
    client: form.client.trim(),
    shoot_date: form.shoot_date,
    call_time: form.call_time || "",
    location_type: form.location_type,
    location: form.location || "",
    shoot_type: form.shoot_type || "",
    crew: form.crew,
    notes: form.notes || "",
    rental_equipment: form.rental_equipment || "",
    items: Object.values(form.items),
  });

  const save = async () => {
    if (!validReady) return toast.error("Fill client, date, location and pick at least one item");
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = id ? await api.patch(`/shoots/${id}`, payload) : await api.post("/shoots", payload);
      toast.success(id ? "Shoot updated" : "Shoot saved");
      if (!id) nav(`/shoots/${res.data.id}/edit`, { replace: true });
      return res.data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const saveAndDownload = async () => {
    setDownloading(true);
    const saved = await save();
    if (saved) buildShootPdf(saved);
    setDownloading(false);
  };

  const shareWhatsApp = async () => {
    const saved = await save();
    if (!saved) return;
    const url = shootWhatsAppUrl(saved);
    // On mobile browsers, `window.open` after an async await loses the user
    // gesture context and gets dropped by popup blockers. Using an anchor
    // click (or location.href as a fallback) hands off reliably to the
    // native WhatsApp app on iOS + Android.
    try {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      window.location.href = url;
    }
  };

  const totalItems = useMemo(() => Object.values(form.items).reduce((n, it) => n + (it.qty || 1), 0), [form.items]);
  const groupedSelected = useMemo(() => {
    const map = new Map();
    Object.values(form.items).forEach((it) => {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category).push(it);
    });
    return [...map.entries()];
  }, [form.items]);

  if (!canView) return <div className="p-6 text-white/60">Shoot Team access only.</div>;

  return (
    <div className="p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6" data-testid="new-shoot-page">
      {/* LEFT: form */}
      <div className="space-y-5">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-white/40 flex items-center gap-2">
            <ClipboardList size={14} /> Pre-shoot Checklist
          </div>
          <h1 className="text-3xl sm:text-4xl font-display tracking-tight">{id ? "Edit shoot" : "Plan a shoot"}{loadingShoot && <span className="ml-3 text-base text-white/40 font-mono">· loading…</span>}</h1>
          <p className="text-base text-white/50 mt-1">Every gear + crew + location detail before crew rolls out.</p>
        </div>

        {/* Saved Shoots list moved to the right sidebar below Summary. */}

        {/* Meta */}
        <div className="rounded-lg border border-white/10 bg-black/25 p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="overline">Client <span className="text-[#E11D2E]">*</span></Label>
              <Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="Client name" data-testid="shoot-client" className="bg-black/40 border-white/10 mt-1.5" />
            </div>
            <div>
              <Label className="overline">Shoot type</Label>
              <Input value={form.shoot_type} onChange={(e) => setForm({ ...form, shoot_type: e.target.value })} placeholder="Product / Interview / Ad film / …" data-testid="shoot-type" className="bg-black/40 border-white/10 mt-1.5" />
            </div>
            <div>
              <Label className="overline">Date <span className="text-[#E11D2E]">*</span></Label>
              <Input type="date" value={form.shoot_date} onChange={(e) => setForm({ ...form, shoot_date: e.target.value })} data-testid="shoot-date" className="bg-black/40 border-white/10 mt-1.5 [color-scheme:dark]" />
            </div>
            <div>
              <Label className="overline">Call time</Label>
              <Input type="time" value={form.call_time} onChange={(e) => setForm({ ...form, call_time: e.target.value })} data-testid="shoot-call-time" className="bg-black/40 border-white/10 mt-1.5 [color-scheme:dark]" />
            </div>
          </div>

          {/* Location toggle */}
          <div>
            <Label className="overline">Location <span className="text-[#E11D2E]">*</span></Label>
            <div className="flex gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setLocationType("Studio")}
                data-testid="shoot-loc-studio"
                className={`flex-1 rounded-md border px-3 py-2 text-sm flex items-center justify-center gap-2 transition ${form.location_type === "Studio" ? "border-[#E11D2E] bg-[#E11D2E]/10 text-white" : "border-white/15 bg-black/30 text-white/60 hover:border-white/30"}`}
              ><Building2 size={14} /> Studio</button>
              <button
                type="button"
                onClick={() => setLocationType("Outside")}
                data-testid="shoot-loc-outside"
                className={`flex-1 rounded-md border px-3 py-2 text-sm flex items-center justify-center gap-2 transition ${form.location_type === "Outside" ? "border-[#E11D2E] bg-[#E11D2E]/10 text-white" : "border-white/15 bg-black/30 text-white/60 hover:border-white/30"}`}
              ><MapPin size={14} /> Outside</button>
            </div>
            {form.location_type === "Outside" && (
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Address / venue" data-testid="shoot-location-address" className="bg-black/40 border-white/10 mt-2" />
            )}
            {form.location_type === "Studio" && (
              <div className="text-sm text-white/40 mt-1.5 font-mono">Life Inside Media Studio, Pune</div>
            )}
          </div>

          {/* Crew */}
          <div>
            <Label className="overline flex items-center gap-2"><Users size={12} /> Crew</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {form.crew.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-sm bg-white/5 border border-white/15 rounded-full pl-3 pr-1.5 py-1.5" data-testid={`shoot-crew-chip-${i}`}>
                  {c.name}
                  {c.is_manager && <span className="text-[0.7rem] font-mono text-red-300 bg-red-500/10 rounded-full px-1.5 py-0.5 ml-0.5">MGR</span>}
                  <button type="button" onClick={() => removeCrew(i)} className="h-5 w-5 rounded-full hover:bg-white/10 flex items-center justify-center"><X size={12} /></button>
                </span>
              ))}
              <div className="inline-flex items-center gap-1">
                <Input
                  list="crew-suggestions"
                  value={newCrew}
                  onChange={(e) => setNewCrew(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCrew(); } }}
                  placeholder="+ Add member"
                  data-testid="shoot-crew-add-input"
                  className="bg-black/40 border-white/10 h-10 w-44 text-base"
                />
                <datalist id="crew-suggestions">
                  {users.map((u) => <option key={u.id} value={u.name} />)}
                </datalist>
                <Button size="sm" onClick={addCrew} className="h-10 bg-white/10 hover:bg-white/15" data-testid="shoot-crew-add-btn"><Plus size={14} /></Button>
              </div>
            </div>
          </div>

          {/* Rental Equipment */}
          <div>
            <Label className="overline">Rental Equipment</Label>
            <Textarea value={form.rental_equipment} onChange={(e) => setForm({ ...form, rental_equipment: e.target.value })} placeholder="Vendor gear you're renting from outside (one per line)" data-testid="shoot-rental" className="bg-black/40 border-white/10 mt-1.5 min-h-[70px]" />
          </div>

          {/* Notes */}
          <div>
            <Label className="overline">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional briefs, references, wardrobe notes…" data-testid="shoot-notes" className="bg-black/40 border-white/10 mt-1.5 min-h-[70px]" />
          </div>
        </div>

        {/* Equipment picker */}
        <div className="rounded-lg border border-white/10 bg-black/25 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg">Equipment picker</h3>
            <span className="text-sm text-white/40 font-mono">{Object.keys(form.items).length} lines · {totalItems} total</span>
          </div>
          {cats.length === 0 && (
            <div className="text-white/50 text-sm">No categories yet — visit Equipment to add some.</div>
          )}
          <div className="space-y-2">
            {cats.map((cat) => {
              const list = items.filter((i) => i.category === cat.name);
              const sel = list.filter((i) => isPicked(i)).length;
              return (
                <div key={cat.id} className="border border-white/10 rounded-md bg-black/20" data-testid={`shoot-picker-cat-${cat.id}`}>
                  <button type="button" onClick={() => toggleCat(cat.name)} className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/[0.03]">
                    {catOpen(cat.name) ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    <span className="text-base font-medium">{cat.name}</span>
                    <span className="text-sm text-white/40 font-mono ml-auto">{sel}/{list.length}</span>
                  </button>
                  {catOpen(cat.name) && (
                    <div className="border-t border-white/5">
                      <div className="flex items-center gap-2 px-3 py-2 text-xs text-white/50 bg-black/20">
                        <button type="button" onClick={() => selectAllInCategory(cat.name)} className="hover:text-white uppercase tracking-widest">Select all</button>
                        <span className="text-white/20">·</span>
                        <button type="button" onClick={() => clearCategory(cat.name)} className="hover:text-white uppercase tracking-widest">Clear</button>
                      </div>
                      <div className="divide-y divide-white/5">
                        {list.map((it) => {
                          const on = isPicked(it);
                          return (
                            <div key={it.id} className={`flex items-center gap-3 px-3 py-2.5 ${on ? "bg-[#E11D2E]/[0.05]" : ""}`}>
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => togglePick(it)}
                                className="accent-[#E11D2E] h-4 w-4"
                                data-testid={`shoot-pick-${it.id}`}
                              />
                              {it.photo_url ? (
                                <img src={it.photo_url} alt="" className="h-9 w-9 rounded object-cover border border-white/10" />
                              ) : (
                                <div className="h-9 w-9 rounded border border-dashed border-white/15" />
                              )}
                              <div className="flex-1 text-base truncate">{it.name}</div>
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => stepQty(it, -1)} className="h-8 w-8 rounded bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70" title="Reduce"><Minus size={13} /></button>
                                <span className="text-base font-mono w-7 text-center">{on ? pickedQty(it) : it.default_qty}</span>
                                <button onClick={() => stepQty(it, 1)} className="h-8 w-8 rounded bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70" title="Add"><Plus size={13} /></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* RIGHT: sticky summary + Saved Shoots archive */}
      <div className="space-y-4">
      <div className="xl:sticky xl:top-4 self-start rounded-lg border border-white/10 bg-black/25 p-4 h-fit">
        <div className="overline mb-2 flex items-center gap-2"><Check size={12} /> Summary</div>
        <div className="text-sm text-white/50 space-y-1 mb-3">
          <div><b className="text-white/80">Client:</b> {form.client || "—"}</div>
          <div><b className="text-white/80">Date:</b> {form.shoot_date || "—"}{form.call_time ? "  ·  " + form.call_time : ""}</div>
          <div><b className="text-white/80">Location:</b> {form.location_type ? `${form.location_type} — ${form.location}` : "— (pick one)"}</div>
          <div><b className="text-white/80">Crew:</b> {form.crew.map((c) => c.name).join(", ") || "—"}</div>
        </div>
        <div className="border-t border-white/5 pt-3">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-1.5">{Object.keys(form.items).length} items · {totalItems} total</div>
          <div className="max-h-72 overflow-y-auto pr-1 space-y-2 text-sm" data-testid="shoot-summary-list">
            {groupedSelected.length === 0 && <div className="text-white/40 italic">Pick items on the left…</div>}
            {groupedSelected.map(([cat, list]) => (
              <div key={cat}>
                <div className="text-xs text-red-300 uppercase tracking-widest mb-1">{cat}</div>
                <ul className="space-y-0.5">
                  {list.map((it) => <li key={itemKey(it)} className="text-white/80 flex justify-between gap-2"><span className="truncate">{it.name}</span><span className="text-red-300 font-mono shrink-0">× {it.qty}</span></li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-white/5 mt-3 pt-3 space-y-2">
          <Button onClick={save} disabled={!validReady || saving} className="w-full bg-[#E11D2E] hover:bg-[#F03041] rounded-md h-10 text-sm" data-testid="shoot-save-btn">
            <Save size={15} className="mr-1.5" /> {saving ? "Saving…" : id ? "Update shoot" : "Save shoot"}
          </Button>
          <Button onClick={saveAndDownload} disabled={!validReady || saving || downloading} variant="outline" className="w-full border-white/15 hover:border-red-500/40 h-10 text-sm" data-testid="shoot-pdf-btn">
            <Download size={15} className="mr-1.5" /> {downloading ? "Building PDF…" : "Save + Download PDF"}
          </Button>
          <Button onClick={shareWhatsApp} disabled={!validReady || saving} variant="outline" className="w-full border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 h-10 text-sm" data-testid="shoot-wa-btn">
            <Send size={15} className="mr-1.5" /> Send on WhatsApp
          </Button>
        </div>
        {!validReady && <div className="text-xs text-amber-300/70 mt-2">Client, date, location + ≥1 item required.</div>}
      </div>

      {/* Saved Shoots archive — all previous entries, click to edit */}
      <SavedShootsArchive
        shoots={recentShoots}
        currentId={id}
        onEdit={(sid) => nav(`/shoots/${sid}/edit`)}
        onCopy={applyPrevious}
        onDelete={deleteShoot}
        canCopy={true}
        applyOpts={applyOpts}
        setApplyOpts={setApplyOpts}
      />
      </div>
    </div>
  );
}

function SavedShootsArchive({ shoots, currentId, onEdit, onCopy, onDelete, canCopy, applyOpts, setApplyOpts }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return shoots;
    return shoots.filter((s) =>
      (s.client || "").toLowerCase().includes(needle) ||
      (s.shoot_type || "").toLowerCase().includes(needle) ||
      (s.location || "").toLowerCase().includes(needle) ||
      (s.shoot_date || "").includes(needle)
    );
  }, [shoots, q]);
  return (
    <div className="rounded-lg border border-white/10 bg-black/25 p-4" data-testid="shoots-archive">
      <div className="flex items-center justify-between mb-2">
        <div className="overline flex items-center gap-2"><ClipboardList size={13} /> Saved shoots</div>
        <div className="text-xs text-white/40 font-mono">{filtered.length} of {shoots.length}</div>
      </div>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search client, date, type…"
        data-testid="shoots-archive-search"
        className="bg-black/40 border-white/10 mb-2 h-9 text-sm"
      />
      {canCopy && (
        <div className="flex items-center gap-3 text-xs text-white/60 mb-2">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={applyOpts.equipment} onChange={(e) => setApplyOpts((o) => ({ ...o, equipment: e.target.checked }))} className="accent-[#E11D2E] h-3.5 w-3.5" />
            copy equipment
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={applyOpts.crew} onChange={(e) => setApplyOpts((o) => ({ ...o, crew: e.target.checked }))} className="accent-[#E11D2E] h-3.5 w-3.5" />
            copy crew
          </label>
        </div>
      )}
      <div className="max-h-[520px] overflow-y-auto pr-1 space-y-1.5">
        {filtered.length === 0 && (
          <div className="text-sm text-white/40 italic py-2">
            {shoots.length === 0 ? "No shoots saved yet." : "No matches."}
          </div>
        )}
        {filtered.map((s) => {
          const isCurrent = currentId === s.id;
          return (
            <div
              key={s.id}
              data-testid={`shoots-archive-row-${s.id}`}
              className={`group rounded-md border p-2.5 transition ${
                isCurrent
                  ? "border-[#E11D2E]/60 bg-[#E11D2E]/10"
                  : "border-white/10 bg-black/30 hover:border-white/25"
              }`}
            >
              <button
                type="button"
                onClick={() => !isCurrent && onEdit(s.id)}
                data-testid={`shoots-archive-edit-${s.id}`}
                disabled={isCurrent}
                className="w-full text-left"
              >
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-medium truncate flex-1">{s.client || "Untitled"}</div>
                  {isCurrent && <span className="text-[0.7rem] uppercase tracking-widest text-red-300 font-mono">Editing</span>}
                </div>
                <div className="text-xs text-white/50 font-mono truncate mt-0.5">
                  {s.shoot_date}
                  {s.call_time ? " · " + s.call_time : ""}
                  {s.location_type ? " · " + s.location_type : ""}
                </div>
                <div className="text-xs text-white/40 font-mono truncate">
                  {(s.items?.length || 0)} item{s.items?.length === 1 ? "" : "s"} · {(s.crew?.length || 0)} crew
                  {s.shoot_type ? " · " + s.shoot_type : ""}
                </div>
              </button>
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/5">
                {!isCurrent && (
                  <button
                    onClick={() => onEdit(s.id)}
                    data-testid={`shoots-archive-edit-btn-${s.id}`}
                    className="text-xs px-2 py-1 rounded text-white/70 hover:text-white hover:bg-white/10"
                  >
                    Edit
                  </button>
                )}
                {canCopy && (
                  <button
                    onClick={() => onCopy(s)}
                    data-testid={`shoots-archive-copy-${s.id}`}
                    className="text-xs px-2 py-1 rounded text-emerald-300/90 hover:text-emerald-200 hover:bg-emerald-500/10 inline-flex items-center gap-1"
                    title={currentId ? "Start a fresh draft from this shoot (next day)" : "Copy equipment / crew into current draft"}
                  >
                    <Copy size={12} /> {currentId ? "Copy for next day" : "Copy"}
                  </button>
                )}
                <button
                  onClick={() => onDelete(s)}
                  data-testid={`shoots-archive-delete-${s.id}`}
                  className="ml-auto text-xs px-2 py-1 rounded text-red-300/80 hover:text-red-200 hover:bg-red-500/10 inline-flex items-center gap-1"
                  title="Delete this shoot"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
